import React, { useState, useRef } from 'react';
import { DatePicker, List, Typography, Space, Popover, Button } from 'antd';
import { DownOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Text } = Typography;

/**
 * RangeOrPrefilledPicker
 *
 * Props:
 * - prefilledPeriods: Array<{ label: string, scheduleId: string, start?: dayjs, end?: dayjs }>
 * - value: { type: 'range', range: [dayjs, dayjs] } | { type: 'prefilled', prefilled: object } | null
 * - onChange: (value) => void
 * - disabled: boolean
 */
function RangeOrPrefilledPicker({ schedule, prefilledPeriods = [], value, onChange, disabled }) {
  const filteredPrefilledPeriods = prefilledPeriods.filter(p => p.scheduleId !== schedule.scheduleId);
  const [open, setOpen] = useState(false);
  const [selectedPrefilledIdx, setSelectedPrefilledIdx] = useState(
    value?.type === 'prefilled' ? filteredPrefilledPeriods.findIndex(p => p.scheduleId === value.prefilled?.scheduleId) : null
  );
  const [customRange, setCustomRange] = useState(value?.start && value?.end ? [value.start, value.end] : [null, null]);
  const triggerRef = useRef();

  // Display string for the current value
  let displayValue = 'Select period';
  if(value?.scheduleName) {
    displayValue = value.scheduleName;
  } else if (value?.start && value?.end) {
    displayValue = `${value.start.format('DD/MM/YYYY')} - ${value.end.format('DD/MM/YYYY')}`;
  }

  // Handle custom range change
  const handleRangeChange = (range) => {
    setCustomRange(range);
    setSelectedPrefilledIdx(null);
    if (onChange) {
      onChange({ type: 'range', range });
    }
    setOpen(false);
  };

  // Handle prefilled selection
  const handlePrefilledSelect = (item, idx) => {
    setSelectedPrefilledIdx(idx);
    setCustomRange([null, null]);
    if (onChange) {
      onChange({ type: 'prefilled', prefilled: item });
    }
    setOpen(false);
  };

  // Panel content
  const panel = (
    <div style={{ display: 'flex', gap: 16, minWidth: 420, maxWidth: 500 }}>
      <div style={{ flex: 1 }}>
        <Text strong>Custom Range</Text>
        <RangePicker
          value={customRange}
          onChange={handleRangeChange}
          disabled={disabled}
          style={{ width: '100%' }}
          autoFocus
        />
      </div>
      <div style={{ flex: 1, minWidth: 200, maxHeight: 190, overflow: 'hidden' }}>
        <Text strong>Prefilled Periods</Text>
        <List
          style={{ 
            maxHeight: 160,
            overflowY: 'auto'
          }}
          bordered
          size="small"
          dataSource={filteredPrefilledPeriods}
          renderItem={(item, idx) => (
            <List.Item
              style={{
                background: selectedPrefilledIdx === idx ? '#f0f5ff' : undefined,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px',
              }}
              onClick={() => handlePrefilledSelect(item, idx)}
            >
              <Space direction="vertical" size={0}>
                <Text>{item.label}</Text>
                {item.start && item.end && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {item.start.format('DD/MM/YYYY')} - {item.end.format('DD/MM/YYYY')}
                  </Text>
                )}
              </Space>
            </List.Item>
          )}
        />
      </div>
    </div>
  );

  return (
    <Popover
      content={panel}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="right"
      overlayStyle={{ 
        padding: 0,
        position: 'fixed'
      }}
      getPopupContainer={() => document.body}
    >
      <Button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        disabled={disabled}
        style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        icon={<CalendarOutlined style={{ color: '#888' }} />}
      >
        <span>{displayValue}</span>
        <DownOutlined style={{ marginLeft: 8, color: '#888' }} />
      </Button>
    </Popover>
  );
}

export default RangeOrPrefilledPicker; 